import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import Swal from "sweetalert2";
import { FileUploadService } from "../../../core/services/default/file-upload.service";

import {
  error as customError,
  errorHtml,
  successHtml,
} from "../../../utils/swal.model";

@Component({
  selector: 'app-upload-documents',
  templateUrl: './upload-documents.component.html',
  styleUrls: ['./upload-documents.component.scss']
})
export class UploadDocumentsComponent implements OnInit, OnChanges {

  @Input() documents: { kind: string; status: string }[] = [];
  @Input() is_verified: boolean | null = null;
  @Output() onSubmitDocuments = new EventEmitter<{ identification: string, selfie: string }>();
  @ViewChild('uploadDocumentInput') uploadDocumentInput: ElementRef;
  @ViewChild('uploadSelfieInput') uploadSelfieInput: ElementRef;

  formDocuments: FormGroup;
  loading = false;
  formSubmitted = false;

  preview: {
    [key: string]: {
      fileName: string;
      url?: string;
      path?: string | null;
    }
  } = {};
  loadingUpload: { [key: string]: boolean } = {};

  documentsStatus = {
    identification: null as string | null,
    selfie: null as string | null
  };

  hasDocumentsLoaded = false;

  constructor(
    private fb: FormBuilder,
    private fileUploadService: FileUploadService,
  ) {
    this.formDocuments = this.fb.group({
      identificationFile: [null, Validators.required],
      selfieFile: [null, Validators.required]
    });
  }

  ngOnInit(): void {
    if (this.documents && this.documents.length > 0) {
      this.updateDocumentsStatus();
      this.hasDocumentsLoaded = true;
    }
    this.updateValidatorsBasedOnStatus();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['documents'] && this.documents) {
      this.updateDocumentsStatus();
      this.hasDocumentsLoaded = true;
      this.updateValidatorsBasedOnStatus();
    }
  }

  private updateDocumentsStatus(): void {
    const getLatestStatus = (kind: string) => {
      const filtered = this.documents
        .filter(d => d.kind === kind)
        .sort((a: any, b: any) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());

      return filtered[0]?.status || null;
    };

    this.documentsStatus.identification = getLatestStatus('identification');
    this.documentsStatus.selfie = getLatestStatus('selfie');
  }

  areAllDocumentsApproved(): boolean {
    return (
      this.documentsStatus.identification === 'approved' &&
      this.documentsStatus.selfie === 'approved'
    );
  }

  private updateValidatorsBasedOnStatus(): void {
    const selfieControl = this.formDocuments.get('selfieFile');
    const idControl = this.formDocuments.get('identificationFile');

    if (['approved', 'processing', 'pending_manual_analysis'].includes(this.documentsStatus.selfie || '')) {
      selfieControl?.clearValidators();
    } else {
      selfieControl?.setValidators([Validators.required]);
    }

    if (['approved', 'processing', 'pending_manual_analysis'].includes(this.documentsStatus.identification || '')) {
      idControl?.clearValidators();
    } else {
      idControl?.setValidators([Validators.required]);
    }

    selfieControl?.updateValueAndValidity({ emitEvent: false });
    idControl?.updateValueAndValidity({ emitEvent: false });
  }

  getDocumentStatusText(status: string): string {
    switch (status) {
      case 'approved': return 'Aprovado';
      case 'requested': return 'Reenvio solicitado';
      case 'processing':
      case 'pending_manual_analysis': return 'Em análise';
      case 'rejected': return 'Rejeitado';
      default: return 'Não enviado';
    }
  }

  getDocumentStatusClass(status: string): string {
    switch (status) {
      case 'approved': return 'status-approved';
      case 'requested':
      case 'rejected': return 'status-rejected';
      case 'processing':
      case 'pending_manual_analysis': return 'status-pending';
      default: return 'status-empty';
    }
  }

  canUpload(status: string): boolean {
    return ['requested', 'rejected'].includes(status);
  }

  async onFileChange(event: any, controlName: 'identificationFile' | 'selfieFile') {
    const file: File = event.target.files[0];
    const validTypes = ['image/jpeg', 'image/png', 'application/pdf'];
    const isImage = ['image/jpeg', 'image/png'].includes(file?.type);
    const isPdf = file?.type === 'application/pdf';
    const isValidSize = file?.size <= 5 * 1024 * 1024; // 5MB

    if (!file) return;

    if (!validTypes.includes(file.type)) {
      this.formDocuments.get(controlName)?.setErrors({ invalidType: true });
      return;
    }

    if (!isValidSize) {
      this.formDocuments.get(controlName)?.setErrors({ maxSize: true });
      return;
    }

    this.loadingUpload[controlName] = true;

    try {
      const userData = JSON.parse(localStorage.getItem("user"));
      const category = controlName === 'identificationFile' ? 0 : 1;

      const result = await this.uploadFileToIugu(file, category, userData.userToken.id);

      if (result?.url) {
        this.formDocuments.patchValue({ [controlName]: file });
        this.formDocuments.get(controlName)?.updateValueAndValidity();

        this.preview[controlName] = {
          fileName: file.name,
          url: isPdf ? 'assets/icons/pdf-icon.svg' : '',
          path: result.path || '',
        };

        if (isImage) {
          const reader = new FileReader();
          reader.onload = () => {
            this.preview[controlName].url = reader.result as string;
          };
          reader.readAsDataURL(file);
        }
      }
    } catch (err: any) {
      console.error('Falha no upload automático:', err);
      this.formDocuments.get(controlName)?.setErrors({ uploadFailed: true });

      const backendMessage = err?.error?.message || 'Erro desconhecido';
      const backendDetails = err?.error?.details || '';

      Swal.fire({
        ...customError,
        html: errorHtml(backendMessage, backendDetails),
      });
    } finally {
      this.loadingUpload[controlName] = false;
    }
  }

  async uploadFileToIugu(file: File, category: number, accessId: number) {
    try {
      const result = await this.fileUploadService
        .uploadIuguFile(file, category, accessId)
        .toPromise();

      return result;
    } catch (err) {
      console.error('Erro ao enviar arquivo Iugu:', err);
      throw err;
    }
  }


  async removeFile(controlName: 'identificationFile' | 'selfieFile') {
    const file = this.preview[controlName];

    if (file?.path) {
      try {
        const result = await this.fileUploadService.removeFileFromS3(file.path).toPromise();
        if (!result?.success) {
          throw new Error('Não foi possível remover o arquivo do servidor');
        }
      } catch (err: any) {
        console.error('Erro ao remover arquivo do S3:', err);
        const backendMessage = err?.error?.message || 'Erro ao remover arquivo';
        const backendDetails = err?.error?.details || '';
        Swal.fire({
          ...customError,
          html: errorHtml(backendMessage, backendDetails),
        });
        return;
      }
    }

    this.formDocuments.patchValue({ [controlName]: null });
    this.formDocuments.get(controlName)?.setErrors({ required: true });
    this.preview[controlName] = null;

    if (controlName === 'identificationFile' && this.uploadDocumentInput) {
      this.uploadDocumentInput.nativeElement.value = '';
    } else if (this.uploadSelfieInput) {
      this.uploadSelfieInput.nativeElement.value = '';
    }
  }

  private async toBase64IfNeeded(file: any): Promise<string> {
    if (typeof file === 'string' && file.startsWith('data:')) return file;

    if (file instanceof File) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = (err) => reject(err);
        reader.readAsDataURL(file);
      });
    }

    return '';
  }

  async submitDocuments() {
    this.formSubmitted = true;
    if (!this.formDocuments.valid) return;

    this.loading = true;

    try {
      const identificationFile = this.formDocuments.get('identificationFile')?.value;
      const selfieFile = this.formDocuments.get('selfieFile')?.value;

      const identificationBase64 = identificationFile
        ? await this.toBase64IfNeeded(identificationFile)
        : null;
      const selfieBase64 = selfieFile
        ? await this.toBase64IfNeeded(selfieFile)
        : null;

      if (!identificationBase64 && !selfieBase64) {
        Swal.fire({
          icon: 'warning',
          title: 'Envie ao menos um arquivo',
          text: 'Você precisa selecionar um documento ou selfie para reenviar.'
        });
        this.loading = false;
        return;
      }

      const userData = JSON.parse(localStorage.getItem("user"));
      const accessId = userData?.userToken?.id;

      const payload: any = { accessId, files: {} };
      if (identificationBase64) payload.files.identification = identificationBase64;
      if (selfieBase64) payload.files.selfie = selfieBase64;

      await this.fileUploadService.updateFile(payload).toPromise();

      if (identificationBase64) {
        this.documentsStatus.identification = 'processing';
        this.preview['identificationFile'] = null;
        this.formDocuments.patchValue({ identificationFile: null });
      }
      if (selfieBase64) {
        this.documentsStatus.selfie = 'processing';
        this.preview['selfieFile'] = null;
        this.formDocuments.patchValue({ selfieFile: null });
      }
      this.updateValidatorsBasedOnStatus();
      this.onSubmitDocuments.emit({ identification: identificationBase64, selfie: selfieBase64 });

      const customCss = {
        confirmButtonText: "Continuar",
        customClass: {
          confirmButton: "c-sweetCustom_btnConfirm",
        },
      };
      Swal.fire({
        ...customCss, html: successHtml(
          "Documentos enviados com sucesso",
          "Aguarde a análise de verificação de identidade"
        )
      });
    } catch (error: any) {
      console.error('Erro ao enviar documentos:', error);
      const backendMessage = error?.error?.message || 'Erro ao enviar documentos';
      const backendDetails = error?.error?.details || '';

      Swal.fire({
        ...customError,
        html: errorHtml(backendMessage, backendDetails),
      });
    } finally {
      this.loading = false;
    }
  }


}
